/**
 * DOM overlays over the viewport (architecture §6.3): zone labels, placeholder
 * labels, the selection bracket, the axis triad, the navigation hint, the
 * stats readout and the camera preview's frame. Game View hides them all, as
 * Unreal's does.
 *
 * Positions come from engine-side projection (ViewportCamera.project), so the
 * renderer is never queried. Text stays crisp at any DPR, is themed through CSS
 * variables, and is readable by assistive technology.
 */
import { useCallback, useEffect, useState } from "react";
import { aabbCenter } from "../../core/math/aabb";
import { mat4GetTranslation } from "../../core/math/mat4";
import type { Vec3 } from "../../core/math/vec3";
import { useEditor } from "../EditorContext";
import { useEditorSettings, useSelection, useWorldSnapshot } from "../hooks/editorHooks";
import { useStore } from "../hooks/useStore";
import { screenRectOfBounds, triadAxes } from "./overlayLayout";
import { statsLines, statsOpen, statsSummary } from "./statsLines";
import { idleReason } from "./ambientEffects";
import { useEditorLayout } from "../shell/EditorLayoutContext";
import type { ViewportStatsInput } from "./statsLines";
import { ChevronDownIcon, ChevronRightIcon } from "../ui/icons";
import { navHints } from "./navHints";
import { usePointerKind } from "../hooks/usePointerKind";

/** Zone labels float this far above the top of the zone's contents (meters). */
const LABEL_LIFT = 0.35;
/** Placeholder labels sit this far below the figure's base (meters). */
const PLACEHOLDER_LABEL_DROP = 0.05;

export function ViewportOverlays() {
  // Re-render when the camera moves or the drawn scene changes.
  const { camera, bridge } = useEditor();
  useStore(camera);
  useStore(bridge.revision);
  const { gameView } = useEditorSettings();
  if (gameView) return null;

  return (
    <div className="kn-overlays" aria-hidden="false">
      <EntityLabels />
      <SelectionBracket />
      <AxisTriad />
      <NavHint />
      <ViewportStats />
      <CameraPreviewFrame />
    </div>
  );
}

/**
 * Frames the camera preview the renderer draws in the corner (Unity's Camera
 * Preview) and names the camera it shows.
 */
function CameraPreviewFrame() {
  const { cameraPreview } = useEditor();
  const preview = useStore(cameraPreview);
  if (!preview) return null;

  const { x, y, width, height } = preview.rect;
  return (
    <div
      className="kn-camera-preview"
      style={{ left: x, top: y, width, height }}
      role="img"
      aria-label={`${preview.name} preview`}
    >
      <span className="kn-camera-preview__label">{preview.name}</span>
    </div>
  );
}

/** Chrome's non-standard `performance.memory`; other browsers don't report the heap. */
function jsHeapBytes(): number | null {
  const memory = (performance as { memory?: { usedJSHeapSize: number } }).memory;
  return memory ? memory.usedJSHeapSize : null;
}

/** How often the stats readout refreshes: readable, and cheap even at a high frame rate. */
const STATS_REFRESH_MS = 250;

/** Frame rate and render counts (Show › Stats), like Unreal's `stat unit`. */
function ViewportStats() {
  const { showStats } = useEditorSettings();
  return showStats ? <StatsReadout /> : null;
}

/**
 * One line (frame rate and frame time) that opens to the full table, like
 * Unreal's `stat fps` and `stat unit`. Which one shows is a setting, changed
 * through the Command Bus and kept across visits.
 */
function StatsReadout() {
  const { dispatch } = useEditor();
  const { statsDetails } = useEditorSettings();
  const open = statsOpen(statsDetails, useEditorLayout());
  const input = useStatsInput();
  const Chevron = open ? ChevronDownIcon : ChevronRightIcon;

  return (
    // A status region, but not announced: four updates a second would flood a screen reader.
    <div
      className="kn-stats"
      role="status"
      aria-label="Viewport stats"
      aria-live="off"
      data-expanded={open}
    >
      <button
        type="button"
        className="kn-stats__toggle"
        aria-expanded={open}
        title={open ? "Hide stats details" : "Show stats details"}
        onClick={() =>
          void dispatch({
            type: "editor.setSetting",
            // An explicit choice, kept from then on (no longer following the layout).
            payload: { key: "statsDetails", value: open ? "folded" : "open" },
            origin: "viewport",
          })
        }
      >
        <Chevron size={10} />
        {statsSummary(input)}
      </button>
      {open && (
        <dl className="kn-stats__table">
          {/* The first two rows are the summary's. */}
          {statsLines(input)
            .slice(2)
            .map(([label, value]) => (
              <div key={label} className="kn-stats__row">
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
        </dl>
      )}
    </div>
  );
}

/** The readout's numbers, sampled every STATS_REFRESH_MS. */
function useStatsInput(): ViewportStatsInput {
  const { render, camera, reducedMotion } = useEditor();
  const { realtime } = useEditorSettings();
  const read = useCallback(
    (): ViewportStatsInput => ({
      running: render.frameLoop.isRunning,
      timings: render.frameLoop.timings(),
      render: render.backend.stats(),
      api: render.backend.info.api,
      size: camera.viewportSize,
      heapBytes: jsHeapBytes(),
      idleReason: idleReason(realtime, reducedMotion),
    }),
    [render, camera, realtime, reducedMotion],
  );
  const [input, setInput] = useState(read);

  useEffect(() => {
    const timer = setInterval(() => {
      setInput(read());
    }, STATS_REFRESH_MS);
    return () => {
      clearInterval(timer);
    };
  }, [read]);

  return input;
}

function EntityLabels() {
  const { camera, bridge } = useEditor();
  const world = useWorldSnapshot();
  const selection = useSelection();
  const entities = [...world.entities.values()];

  const zoneLabels = entities
    .filter((entity) => entity.components.has("Zone"))
    .map((entity) => {
      const bounds = bridge.boundsOf([entity.id]);
      if (!bounds) return null;
      const center = aabbCenter(bounds);
      const anchor: Vec3 = [center[0], bounds.max[1] + LABEL_LIFT, center[2]];
      return { entity, anchor, kind: "zone" as const, text: entity.name };
    });

  const placeholderLabels = entities
    .filter((entity) => entity.components.has("Placeholder"))
    .map((entity) => {
      const label = entity.components.get("Placeholder")?.label;
      if (typeof label !== "string" || label === "") return null;
      const [x, y, z] = mat4GetTranslation(bridge.worldMatrixOf(entity.id));
      return {
        entity,
        anchor: [x, y - PLACEHOLDER_LABEL_DROP, z] as Vec3,
        kind: "placeholder" as const,
        text: label,
      };
    });

  return (
    <>
      {[...zoneLabels, ...placeholderLabels].map((label) => {
        if (!label || !bridge.isVisible(label.entity.id)) return null;
        const point = camera.project(label.anchor);
        if (!point.visible) return null;
        return (
          <span
            key={`${label.kind}-${label.entity.id}`}
            className={`kn-label kn-label--${label.kind}`}
            data-selected={selection.ids.includes(label.entity.id)}
            style={{ transform: `translate(${point.x}px, ${point.y}px)` }}
          >
            {label.text}
          </span>
        );
      })}
    </>
  );
}

/**
 * Dashed screen rectangle around the active selection, with corner handles
 * (reference 02). Off unless the "Selection bracket" preference is on.
 */
function SelectionBracket() {
  const { camera, bridge } = useEditor();
  const { active } = useSelection();
  const { showSelectionBracket } = useEditorSettings();
  if (active === null || !showSelectionBracket) return null;

  const bounds = bridge.boundsOf([active]);
  if (!bounds) return null;
  const { width, height } = camera.viewportSize;
  const rect = screenRectOfBounds(camera.getSnapshot(), bounds, width, height);
  if (!rect) return null;

  return (
    <div
      className="kn-bracket"
      data-testid="selection-bracket"
      style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
    >
      {["tl", "tr", "bl", "br"].map((corner) => (
        <span key={corner} className={`kn-bracket__handle kn-bracket__handle--${corner}`} />
      ))}
    </div>
  );
}

const TRIAD_ORIGIN = { x: 16, y: 30 };
const TRIAD_LENGTH = 18;

function AxisTriad() {
  const { camera } = useEditor();
  const axes = triadAxes(camera.getSnapshot());

  return (
    <svg className="kn-triad" width="48" height="48" viewBox="0 0 48 48" aria-label="Axes">
      {axes.map(({ axis, dx, dy }) => {
        const x = TRIAD_ORIGIN.x + dx * TRIAD_LENGTH;
        const y = TRIAD_ORIGIN.y + dy * TRIAD_LENGTH;
        return (
          <g key={axis} className={`kn-triad__axis kn-triad__axis--${axis}`}>
            <line x1={TRIAD_ORIGIN.x} y1={TRIAD_ORIGIN.y} x2={x} y2={y} />
            {axis !== "z" && (
              <text x={x + dx * 5} y={y + dy * 5 + 3} textAnchor="middle">
                {axis.toUpperCase()}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/** Shown until the first successful navigation or until dismissed (SPEC §4.6). */
function NavHint() {
  const { dispatch } = useEditor();
  const { navHintDismissed } = useEditorSettings();
  const pointer = usePointerKind();
  if (navHintDismissed) return null;

  const hints = navHints(pointer);

  return (
    <div className="kn-navhint" data-testid="nav-hint">
      {hints.map(([keys, action]) => (
        <span key={action}>
          <kbd>{keys}</kbd> {action}
        </span>
      ))}
      <button
        type="button"
        className="kn-navhint__close"
        aria-label="Dismiss navigation hint"
        onClick={() =>
          void dispatch({
            type: "editor.setSetting",
            payload: { key: "navHintDismissed", value: true },
            origin: "viewport",
          })
        }
      >
        ×
      </button>
    </div>
  );
}
